import { FC, memo, useMemo } from 'react';
import classnames from 'classnames';

import { useTranslation } from 'react-i18next';
import { translations } from '@shippypro/translations_restricted';

import { MetricTrend, UncontrolledTooltip } from '@ds-web/components';

import { IKpiDataProps, unitPositionEnum } from './types';

import { Spinner } from 'reactstrap';
import { IconHelper } from '@ds-web-iconhelper';

import { useGetDeviceSize } from '@ds-web/utils/hooks';

/**
 * A simple component that manage different state of the KPI data card.
 *
 * @interface IKpiCardProps
 * @author Federico Mauri <federico.mauri@shippypro.com>
 */
export const KpiData: FC<IKpiDataProps> = memo(
  ({
    code,
    kpiUnit,
    unitPosition = unitPositionEnum.RIGHT,
    kpiValue,
    kpiPreviousValue,
    kpiTrendId,
    kpiTrend,
    invertTrendColor = false,
    isFetching = false,
    isError = false,
    trendPopover,
  }) => {
    const { t } = useTranslation();
    const transCommon = translations.common;

    const { isMobile, isTablet } = useGetDeviceSize();
    const isDesktop = !isMobile && !isTablet;

    const tooltipTarget = `${code.replaceAll(' ', '-')}-kpi-data-error-icon`;

    const metricSummaryBody = useMemo(() => {
      if (isFetching) return <Spinner size="sm" />;
      if (isError)
        return (
          <span id={tooltipTarget}>
            <IconHelper
              icon="IconAlertCircleFilled"
              size={18}
              className="text-red-500"
            />
            <UncontrolledTooltip target={tooltipTarget}>
              {t(transCommon.errors['genericAnalytics'])}
            </UncontrolledTooltip>
          </span>
        );

      return (
        <>
          <span
            id="metrics-summary"
            className={classnames('font-bold', {
              'text-black': kpiValue !== '-',
              'text-secondary': kpiValue === '-',
              'text-4xl': isDesktop,
              'text-2xl': !isDesktop,
            })}
            data-test="kpi-value"
          >
            {unitPosition === 'left' ? (
              <>
                {kpiUnit} {kpiValue}
              </>
            ) : (
              <>
                {kpiValue} {kpiUnit}
              </>
            )}
          </span>
          <MetricTrend
            id={kpiTrendId}
            value={kpiTrend}
            previousValue={kpiPreviousValue}
            invertTrendColor={invertTrendColor}
            iconUp="IconArrowUpRight"
            iconDown="IconArrowDownLeft"
            trendPopover={trendPopover}
          />
        </>
      );
    }, [
      isFetching,
      isError,
      tooltipTarget,
      t,
      transCommon.errors,
      kpiValue,
      unitPosition,
      kpiUnit,
      kpiTrendId,
      kpiTrend,
      kpiPreviousValue,
      invertTrendColor,
      trendPopover,
    ]);

    return (
      <div
        id="metrics-summary-block"
        className="w-full flex justify-between items-baseline !gap-4"
      >
        {metricSummaryBody}
      </div>
    );
  },
);
